"use client";

import { c as _c } from "react/compiler-runtime";
import type { CSSProperties, FC } from "react";
import { format as formatDate } from "date-fns";
import { autoformatArrow, autoformatLegal, autoformatLegalHtml, autoformatMath, autoformatPunctuation, autoformatSmartQuotes, type AutoformatRule, AutoformatPlugin } from "@platejs/autoformat";
import { BlockquotePlugin, HeadingPlugin, HighlightPlugin, BoldPlugin, CodePlugin, ItalicPlugin, StrikethroughPlugin, SubscriptPlugin, SuperscriptPlugin, UnderlinePlugin, KbdPlugin, HorizontalRulePlugin } from "@platejs/basic-nodes/react";
import { LineHeightPlugin, TextAlignPlugin } from "@platejs/basic-styles/react";
import { type SlateEditor, isType, getPluginType } from "@platejs/core";
import { ParagraphPlugin, Plate, usePlateEditor } from "@platejs/core/react";
import { unwrapCodeBlock, insertEmptyCodeBlock } from "@platejs/code-block";
import { CodeLinePlugin, CodeBlockPlugin } from "@platejs/code-block/react";
import { CsvPlugin } from "@platejs/csv";
import { DocxPlugin } from "@platejs/docx";
import { IndentPlugin } from "@platejs/indent/react";
import { JuicePlugin } from "@platejs/juice";
import { LinkPlugin } from "@platejs/link/react";
import { toggleList, unwrapList } from "@platejs/list-classic";
import { BulletedListPlugin, ListItemPlugin, NumberedListPlugin, TodoListPlugin } from "@platejs/list-classic/react";
import { MarkdownPlugin } from "@platejs/markdown";
import { ImagePlugin, MediaEmbedPlugin } from "@platejs/media/react";
import { TabbablePlugin } from "@platejs/tabbable/react";
import { TablePlugin, TableCellPlugin, TableCellHeaderPlugin, TableRowPlugin } from "@platejs/table/react";
import { BlockSelectionPlugin } from "@platejs/selection/react";
import { KEYS, ExitBreakPlugin, NormalizeTypesPlugin, TrailingBlockPlugin } from "@platejs/utils";
import type { NotePopulated } from "@vita-learning/types";
import { Editor } from "./Editor";
import { Toolbar } from "./Toolbar";
import { createPlateUI } from "./createPlateUI";
import { initializePlateValue } from "./initializePlateValue";
import { Container, Footer } from "./styles";
import { HighlightLeaf } from "./HighlightLeaf";
import { KbdLeaf } from "./KbdLeaf";
import { CodeLeaf } from "./CodeLeaf";
import type { PlateValueCallback } from "./types";
const preFormat = (editor: SlateEditor) => unwrapList(editor);
const autoformatBlocks: AutoformatRule[] = [{
  mode: "block",
  type: KEYS.h1,
  match: "# ",
  preFormat
}, {
  mode: "block",
  type: KEYS.h2,
  match: "## ",
  preFormat
}, {
  mode: "block",
  type: KEYS.h3,
  match: "### ",
  preFormat
}, {
  mode: "block",
  type: KEYS.h4,
  match: "#### ",
  preFormat
}, {
  mode: "block",
  type: KEYS.h5,
  match: "##### ",
  preFormat
}, {
  mode: "block",
  type: KEYS.h6,
  match: "###### ",
  preFormat
}, {
  mode: "block",
  type: BlockquotePlugin.key,
  match: "> ",
  preFormat
}, {
  mode: "block",
  type: CodeBlockPlugin.key,
  match: "```",
  triggerAtBlockStart: false,
  preFormat,
  format: (editor: SlateEditor) => {
    insertEmptyCodeBlock(editor, {
      defaultType: getPluginType(editor, ParagraphPlugin.key),
      insertNodesOptions: {
        select: true
      }
    });
  }
}, {
  mode: "block",
  type: HorizontalRulePlugin.key,
  match: ["---", "—-", "___ "],
  format: (editor: SlateEditor) => {
    editor.tf.setNodes({
      type: HorizontalRulePlugin.key
    });
    editor.tf.insertNodes({
      type: ParagraphPlugin.key,
      children: [{
        text: ""
      }]
    });
  }
}];
const format = (editor: SlateEditor, customFormatting: () => void) => {
  if (editor.selection) {
    const parentEntry = editor.api.parent(editor.selection);
    if (!parentEntry) return;
    const [node] = parentEntry;
    if (
    // isElement(node) &&
    !isType(editor, node, CodeBlockPlugin.key) && !isType(editor, node, CodeLinePlugin.key)) {
      customFormatting();
    }
  }
};
const formatList = (editor: SlateEditor, type: string) => format(editor, () => toggleList(editor, {
  type
}));
const autoformatLists: AutoformatRule[] = [{
  mode: "block",
  type: ListItemPlugin.key,
  match: ["* ", "- "],
  preFormat,
  format: (editor: SlateEditor) => formatList(editor, BulletedListPlugin.key)
}, {
  mode: "block",
  type: ListItemPlugin.key,
  match: ["1. ", "1) "],
  preFormat,
  format: (editor: SlateEditor) => formatList(editor, NumberedListPlugin.key)
}, {
  mode: "block",
  type: TodoListPlugin.key,
  match: "[] "
}, {
  mode: "block",
  type: TodoListPlugin.key,
  match: "[x] ",
  format: (editor: SlateEditor) => editor.tf.setNodes({
    type: TodoListPlugin.key,
    checked: true
  }, {
    match: n => editor.api.isBlock(n)
  })
}];
const autoformatMarks: AutoformatRule[] = [{
  mode: "mark",
  type: [BoldPlugin.key, ItalicPlugin.key],
  match: "***"
}, {
  mode: "mark",
  type: [UnderlinePlugin.key, ItalicPlugin.key],
  match: "__*"
}, {
  mode: "mark",
  type: [UnderlinePlugin.key, BoldPlugin.key],
  match: "__**"
}, {
  mode: "mark",
  type: [UnderlinePlugin.key, BoldPlugin.key, ItalicPlugin.key],
  match: "___***"
}, {
  mode: "mark",
  type: BoldPlugin.key,
  match: "**"
}, {
  mode: "mark",
  type: UnderlinePlugin.key,
  match: "__"
}, {
  mode: "mark",
  type: ItalicPlugin.key,
  match: "*"
}, {
  mode: "mark",
  type: ItalicPlugin.key,
  match: "_"
}, {
  mode: "mark",
  type: StrikethroughPlugin.key,
  match: "~~"
}, {
  mode: "mark",
  type: SuperscriptPlugin.key,
  match: "^"
}, {
  mode: "mark",
  type: SubscriptPlugin.key,
  match: "~"
}, {
  mode: "mark",
  type: HighlightPlugin.key,
  match: "=="
}, {
  mode: "mark",
  type: HighlightPlugin.key,
  match: "≡"
}, {
  mode: "mark",
  type: CodePlugin.key,
  match: "`"
}];
export const plugins = [BlockquotePlugin.configure({
  rules: {
    break: {
      empty: "reset"
    },
    delete: {
      start: "reset"
    }
  },
  shortcuts: {
    toggle: {
      keys: "mod+shift+period"
    }
  }
}), CodeBlockPlugin.configure({
  rules: {
    break: {
      default: "lineBreak"
    },
    delete: {
      empty: "reset"
    }
  }
}).overrideEditor(({
  editor,
  tf: {
    resetBlock
  }
}) => ({
  transforms: {
    resetBlock(options) {
      if (editor.api.block({
        at: options?.at,
        match: {
          type: editor.getType(CodeBlockPlugin.key)
        }
      })) {
        unwrapCodeBlock(editor);
        return;
      }
      return resetBlock(options);
    }
  }
})), ParagraphPlugin, HeadingPlugin, HorizontalRulePlugin, LinkPlugin.configure({
  render: {
    // TODO: Implement floating toolbar for links
    // Reference: https://github.com/udecode/plate/blob/main/apps/www/src/registry/default/plate-ui/link-floating-toolbar.tsx
    afterEditable: () => null
  }
}), ImagePlugin, MediaEmbedPlugin, TablePlugin, TableCellPlugin, TableCellHeaderPlugin, TableRowPlugin, BulletedListPlugin.configure({
  shortcuts: {
    toggle: {
      keys: "mod+alt+5"
    }
  }
}), NumberedListPlugin.configure({
  shortcuts: {
    toggle: {
      keys: "mod+alt+6"
    }
  }
}), TodoListPlugin.configure({
  rules: {
    break: {
      empty: "reset"
    },
    delete: {
      start: "reset"
    }
  }
}), ListItemPlugin,
// TODO: Implement plugin for drawing diagrams
// ExcalidrawPlugin,
BoldPlugin, ItalicPlugin, UnderlinePlugin, StrikethroughPlugin.configure({
  shortcuts: {
    toggle: {
      keys: "mod+shift+x"
    }
  }
}), CodePlugin.configure({
  node: {
    component: CodeLeaf
  },
  shortcuts: {
    toggle: {
      keys: "mod+e"
    }
  }
}), SubscriptPlugin.configure({
  shortcuts: {
    toggle: {
      keys: "mod+comma"
    }
  }
}), SuperscriptPlugin.configure({
  shortcuts: {
    toggle: {
      keys: "mod+period"
    }
  }
}), HighlightPlugin.configure({
  shortcuts: {
    toggle: {
      keys: "mod+shift+h"
    }
  },
  node: {
    component: HighlightLeaf
  }
}), KbdPlugin.withComponent(KbdLeaf), TextAlignPlugin.configure({
  inject: {
    targetPlugins: [ParagraphPlugin.key, ...KEYS.heading]
  }
}), IndentPlugin.configure({
  inject: {
    targetPlugins: [ParagraphPlugin.key, ...KEYS.heading]
  }
}), LineHeightPlugin.configure({
  inject: {
    nodeProps: {
      defaultNodeValue: 1.5,
      validNodeValues: [1, 1.2, 1.5, 2, 3]
    },
    targetPlugins: [ParagraphPlugin.key, ...KEYS.heading]
  }
}), AutoformatPlugin.configure({
  options: {
    rules: [...autoformatBlocks, ...autoformatLists, ...autoformatMarks, ...autoformatSmartQuotes, ...autoformatPunctuation, ...autoformatLegal, ...autoformatLegalHtml, ...autoformatArrow, ...autoformatMath],
    enableUndoOnDelete: true
  }
}), BlockSelectionPlugin, ExitBreakPlugin.configure({
  shortcuts: {
    insert: {
      keys: "mod+enter"
    },
    insertBefore: {
      keys: "mod+shift+enter"
    }
  }
}), NormalizeTypesPlugin.configure({
  options: {
    rules: [{
      path: [0],
      strictType: KEYS.h1
    }]
  }
}), TabbablePlugin, TrailingBlockPlugin.configure({
  options: {
    type: ParagraphPlugin.key
  }
}), DocxPlugin, CsvPlugin, MarkdownPlugin, JuicePlugin];
interface TextEditorProps {
  readonly note?: NotePopulated | null;
  readonly toolbar?: boolean;
  readonly sidebar?: boolean;
  readonly isSidebarOpen?: boolean;
  readonly onToggleSidebar?: () => void;
  readonly lastUpdatedTimestamp?: number;
  readonly placeholder?: string;
  readonly style?: CSSProperties;
  readonly streaming?: boolean;
  readonly readOnly?: boolean;
  readonly onChange?: PlateValueCallback;
  readonly onValueChange?: PlateValueCallback;
}
export const TextEditor: FC<TextEditorProps> = t0 => {
  const $ = _c(37);
  const {
    note,
    readOnly,
    lastUpdatedTimestamp,
    style,
    placeholder,
    toolbar: t1,
    sidebar: t2,
    isSidebarOpen: t3,
    onToggleSidebar,
    streaming: t4,
    onChange,
    onValueChange
  } = t0;
  const toolbar = t1 === undefined ? false : t1;
  const sidebar = t2 === undefined ? false : t2;
  const isSidebarOpen = t3 === undefined ? false : t3;
  const streaming = t4 === undefined ? false : t4;
  const t5 = note?.content;
  let t6;
  if ($[0] !== t5) {
    t6 = initializePlateValue(t5);
    $[0] = t5;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  let t7;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = createPlateUI();
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  let t8;
  if ($[3] !== note || $[4] !== streaming) {
    t8 = note ? streaming ? JSON.stringify(note) : `${note.id}-${note.title}` : undefined;
    $[3] = note;
    $[4] = streaming;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  let t9;
  if ($[6] !== t6 || $[7] !== t8) {
    t9 = {
      plugins,
      value: t6,
      components: t7,
      id: t8
    };
    $[6] = t6;
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== note) {
    t10 = [note];
    $[9] = note;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const editor = usePlateEditor(t9, t10);
  let t11;
  if ($[11] !== isSidebarOpen || $[12] !== note || $[13] !== onToggleSidebar || $[14] !== sidebar || $[15] !== toolbar) {
    t11 = toolbar && <Toolbar note={note} sidebar={sidebar} onToggleSidebar={onToggleSidebar} isSidebarOpen={isSidebarOpen} />;
    $[11] = isSidebarOpen;
    $[12] = note;
    $[13] = onToggleSidebar;
    $[14] = sidebar;
    $[15] = toolbar;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = note?.content;
  const t13 = readOnly || false;
  let t14;
  if ($[17] !== placeholder || $[18] !== style || $[19] !== t12 || $[20] !== t13) {
    t14 = <Editor style={style} content={t12} readOnly={t13} placeholder={placeholder} />;
    $[17] = placeholder;
    $[18] = style;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== lastUpdatedTimestamp || $[23] !== note) {
    t15 = lastUpdatedTimestamp && note && <small>Last saved:{" "}{formatDate(lastUpdatedTimestamp, "MMMM dd, yyyy | h:mm:ss a")}</small>;
    $[22] = lastUpdatedTimestamp;
    $[23] = note;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t15) {
    t16 = <Footer>{t15}</Footer>;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== editor || $[28] !== onChange || $[29] !== onValueChange || $[30] !== t11 || $[31] !== t14 || $[32] !== t16) {
    t17 = <Plate onChange={onChange} onValueChange={onValueChange} editor={editor}>{t11}{t14}{t16}</Plate>;
    $[27] = editor;
    $[28] = onChange;
    $[29] = onValueChange;
    $[30] = t11;
    $[31] = t14;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t17 || $[35] !== toolbar) {
    t18 = <Container $toolbar={toolbar}>{t17}</Container>;
    $[34] = t17;
    $[35] = toolbar;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  return t18;
};
export default TextEditor;